import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Copy, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useDomain, useSetDomainMapping, RECOMMENDATION_LABEL } from '@/api/domains';
import { useIsAdmin } from '@/api/hooks';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/PageHeader';
import { DesktopOnly } from '@/components/dashboard/DesktopOnly';
import { CompanySearch, type CompanyOption } from '@/components/CompanySearch';
import { Journey, Pct } from '@/components/domains/DomainsKit';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { ROUTES, companyPath } from '@/lib/routes';
import { cn } from '@/lib/utils';

/**
 * One domain: where it is on the DMARC journey and what the next record should be (dmarcian),
 * who sends as it and how they authenticate, open dmarcian tasks/issues, CIPP's DNS checks, and the
 * record change history.
 */
export default function DomainDetailPage() {
  const { domain } = useParams<{ domain: string }>();
  const { data, isLoading, error, isFetching } = useDomain(domain);
  const d = data?.domain;

  return (
    <DesktopOnly title="Domains is available on desktop" message="Open Cosmos on a larger screen to view domains.">
      <div className="mx-auto w-full max-w-[1400px] px-4 py-4 md:px-6">
        <Link to={ROUTES.domains.root} className="mb-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"><ArrowLeft className="h-3 w-3" /> Domains</Link>
        {isLoading ? <div className="flex items-center gap-2 py-16 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          : error ? <div className="mt-4 text-sm text-destructive">{error instanceof Error ? error.message : 'Failed to load'}</div>
          : data && d ? (
            <>
              <PageHeader title={d.domain} isFetching={isFetching}
                subtitle={<>{d.company.id != null ? <Link to={companyPath(d.company.id)} className="hover:underline">{d.company.name}</Link> : 'Not mapped to a client'}
                  {d.lastReport && <> · last report {fmtRelative(d.lastReport)}</>}</>}
                actions={<Button variant="outline" size="sm" asChild><a href={d.portalUrl} target="_blank" rel="noreferrer"><ExternalLink className="mr-1.5 h-3.5 w-3.5" />Open in dmarcian</a></Button>} />
              <MappingBar domain={d.domain} source={d.company.source} />

              <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
                <Card title="DMARC">
                  <Journey stage={d.stage} />
                  <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                    <Fact label="Compliance (7d)"><Pct value={d.compliancePct} /></Fact>
                    <Fact label="Mail (7d)">{d.volume7d.toLocaleString()}</Fact>
                    <Fact label="At this policy">{d.daysAtPolicy != null ? `${d.daysAtPolicy} days` : '—'}</Fact>
                    <Fact label="Next step">{d.readyToEnforce ? <span className="font-semibold text-warning">Ready to tighten</span> : d.recommendation ? RECOMMENDATION_LABEL[d.recommendation] ?? d.recommendation : '—'}</Fact>
                  </div>
                  {data.impact && (data.impact.impacted_emails ?? 0) > 0 && (
                    <p className="mt-2 text-xs text-warning">Tightening now would affect {data.impact.impacted_emails?.toLocaleString()} of {data.impact.total_emails?.toLocaleString()} messages ({data.impact.impacted_categories?.join(', ')}).</p>
                  )}
                </Card>
                <Card title="Records">
                  <Rec label="Current DMARC" value={data.records.dmarc} note={data.records.dmarcMessage} />
                  {d.recommendedRecord && d.recommendedRecord !== data.records.dmarc && <Rec label="dmarcian recommends" value={d.recommendedRecord} copy />}
                  <Rec label="SPF" value={data.records.spf} note={`${data.records.spfMessage ?? ''}${d.spf.lookups != null ? ` · ${d.spf.lookups}/10 lookups` : ''}`} />
                  <div className="text-xs"><span className="text-muted-foreground">DKIM:</span> {data.records.dkimMessage ?? d.dkim.label ?? '—'}
                    {data.dkimSelectors.length > 0 && <span className="text-muted-foreground"> · selectors {data.dkimSelectors.map((s) => s.selector).join(', ')}</span>}</div>
                </Card>
                <Card title="DNS checks (CIPP)">
                  {data.cipp ? (
                    <>
                      <div className="text-xs"><span className="text-muted-foreground">Domain score:</span> <span className="font-medium">{data.cipp.score ?? '—'}%</span>{data.cipp.checkedAt && <span className="text-muted-foreground"> · {fmtRelative(data.cipp.checkedAt)}</span>}</div>
                      <ul className="mt-2 space-y-0.5 text-xs text-muted-foreground">{data.cipp.notes.map((n) => <li key={n}>· {n}</li>)}</ul>
                    </>
                  ) : <p className="text-xs text-muted-foreground">Not in a CIPP tenant's Domain Analyser.</p>}
                </Card>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
                <Card title={`Senders · last 7 days · ${data.senders.length}`} flush>
                  {data.senders.length === 0 ? <p className="px-4 py-4 text-xs text-muted-foreground">No DMARC-capable senders seen.</p> : (
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                        <tr><th className="px-4 py-2 font-medium">Source</th><th className="px-4 py-2 text-right font-medium">Messages</th><th className="px-4 py-2 text-right font-medium">SPF</th><th className="px-4 py-2 text-right font-medium">DKIM</th><th className="px-4 py-2 text-right font-medium">DMARC</th></tr>
                      </thead>
                      <tbody className="divide-y divide-divider">
                        {data.senders.map((s) => (
                          <tr key={s.name}>
                            <td className="px-4 py-1.5 font-medium text-foreground">{s.name}</td>
                            <td className="px-4 py-1.5 text-right text-xs tabular-nums">{s.messages.toLocaleString()}</td>
                            <td className="px-4 py-1.5 text-right text-xs tabular-nums text-muted-foreground">{s.spfPct}%</td>
                            <td className="px-4 py-1.5 text-right text-xs tabular-nums text-muted-foreground">{s.dkimPct}%</td>
                            <td className="px-4 py-1.5 text-right"><Pct value={s.dmarcPct} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </Card>
                <Card title={`Open tasks & issues · ${data.tasks.length + data.issues.length}`}>
                  {data.tasks.length + data.issues.length === 0 ? <p className="text-xs text-success">Nothing open in dmarcian.</p> : (
                    <ul className="space-y-2 text-xs">
                      {data.issues.map((i) => <li key={`i${i.id}`}><span className="font-medium text-destructive">{i.problem}</span>{i.solution && <div className="text-muted-foreground">{i.solution}</div>}</li>)}
                      {data.tasks.map((t) => <li key={`t${t.id}`}><span className="font-medium text-foreground">{t.category}</span> — <span className="text-muted-foreground">{t.details} · open since {fmtRelative(t.created)}</span></li>)}
                    </ul>
                  )}
                </Card>
              </div>

              <Card title="Record history" className="mt-4">
                {data.history.length === 0 ? <p className="text-xs text-muted-foreground">No DNS changes recorded.</p> : (
                  <ul className="space-y-1.5 text-xs">
                    {data.history.map((h, i) => (
                      <li key={i} className="flex gap-3">
                        <span className="w-24 shrink-0 tabular-nums text-muted-foreground">{h.date.slice(0, 10)}</span>
                        <span className="w-12 shrink-0 font-medium uppercase text-muted-foreground">{h.type}</span>
                        <span className="text-foreground">{h.labels.join(', ')}{h.changes[0]?.new && h.type === 'dmarc' && <span className="text-muted-foreground"> → {h.changes[0].new}</span>}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </>
          ) : null}
      </div>
    </DesktopOnly>
  );
}

function Card({ title, children, className, flush }: { title: string; children: React.ReactNode; className?: string; flush?: boolean }) {
  return (
    <section className={cn('rounded-lg border border-border bg-card shadow-sm', className)}>
      <div className="border-b border-divider px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</div>
      <div className={flush ? '' : 'px-4 py-3'}>{children}</div>
    </section>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">{label}</div><div className="mt-0.5 text-foreground">{children}</div></div>;
}

function Rec({ label, value, note, copy }: { label: string; value: string | null; note?: string | null; copy?: boolean }) {
  return (
    <div className="mb-2">
      <div className="flex items-center justify-between text-[10.5px] uppercase tracking-wider text-muted-foreground">
        {label}
        {copy && value && <button type="button" className="inline-flex items-center gap-1 normal-case text-muted-foreground hover:text-foreground" onClick={() => { void navigator.clipboard.writeText(value); toast.success('Record copied'); }}><Copy className="h-3 w-3" />Copy</button>}
      </div>
      <code className="mt-0.5 block break-all rounded bg-muted px-2 py-1 text-[11px] text-foreground">{value || '—'}</code>
      {note && <div className="mt-0.5 text-[11px] text-muted-foreground">{note}</div>}
    </div>
  );
}

function MappingBar({ domain, source }: { domain: string; source: string }) {
  const { data: isAdmin } = useIsAdmin();
  const set = useSetDomainMapping();
  const [editing, setEditing] = useState(false);
  if (!isAdmin) return null;
  const save = (value: Parameters<typeof set.mutate>[0]['value']) =>
    set.mutate({ domain, value }, { onSuccess: () => { setEditing(false); toast.success('Saved'); }, onError: (e) => toast.error(e instanceof Error ? e.message : 'Could not save') });
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
      <span>Client from {source === 'cipp' ? 'the CIPP tenant' : source === 'parent' ? 'the parent domain' : source === 'manual' ? 'a manual mapping' : source === 'ignored' ? '— ignored' : 'nowhere yet'}.</span>
      {editing ? (
        <>
          <CompanySearch value={null} onChange={(c: CompanyOption | null) => { if (c) save({ companyId: c.id, companyName: c.name }); }} className="w-64" />
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => save({ ignored: true })}>Ignore</Button>
          {(source === 'manual' || source === 'ignored') && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => save(null)}>Reset to automatic</Button>}
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditing(false)}>Cancel</Button>
        </>
      ) : <button type="button" className="hover:text-foreground hover:underline" onClick={() => setEditing(true)}>Change</button>}
    </div>
  );
}
